import { useCallback, useEffect, useRef, useState } from 'react'
import type { ApiClient, PaymentOrder, PaymentProvider } from '../api'
import type { VipStatus } from '../view-types'

type Options = {
  api: ApiClient
  activeUserId: string | null
  ownerGeneration: number
  isCurrentOwner: (generation: number, userId: string | null) => boolean
}

/** Entitlements never enter game saves or device-local membership flags. */
export function useVipEntitlement({ api, activeUserId, ownerGeneration, isCurrentOwner }: Options) {
  const [record, setRecord] = useState({ owner: activeUserId, generation: ownerGeneration, vip: 'unknown' as VipStatus, svip: 'unknown' as VipStatus, error: '' })
  const busy = useRef(false)
  const operation = useRef(0)
  const current = record.owner === activeUserId && record.generation === ownerGeneration
  useEffect(() => {
    const owner = activeUserId
    const generation = ownerGeneration
    const id = ++operation.current
    busy.current = false
    setRecord({ owner, generation, vip: owner ? 'loading' : 'unknown', svip: owner ? 'loading' : 'unknown', error: '' })
    if (!owner) return
    let active = true
    void api.getEntitlements().then(items => {
      if (!active || id !== operation.current || !isCurrentOwner(generation, owner)) return
      const svip = items.some(item => item.productId === 'sun_svip' && item.status === 'active')
      const vip = svip || items.some(item => item.productId === 'sun_vip' && item.status === 'active')
      setRecord({ owner, generation, vip: vip ? 'active' : 'inactive', svip: svip ? 'active' : 'inactive', error: '' })
    }).catch(error => {
      if (active && id === operation.current && isCurrentOwner(generation, owner)) setRecord({ owner, generation, vip: 'error', svip: 'error', error: error instanceof Error ? error.message : '权益服务暂时不可用' })
    })
    return () => { active = false }
  }, [activeUserId, api, isCurrentOwner, ownerGeneration])

  const beginVipPayment = useCallback(async (tier: 'vip' | 'svip', provider: PaymentProvider, mutationId: string, signal?: AbortSignal): Promise<PaymentOrder> => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!owner || busy.current || !isCurrentOwner(generation, owner)) throw new Error('请先登录后再支付。')
    busy.current = true
    setRecord(previous => ({ ...previous, owner, generation, error: '' }))
    try {
      return await api.createPayment(tier, provider, mutationId, signal)
    } catch (error) {
      if (!signal?.aborted && isCurrentOwner(generation, owner)) setRecord(previous => ({ ...previous, error: error instanceof Error ? error.message : '创建支付订单失败，请重试。' }))
      throw error
    } finally {
      busy.current = false
    }
  }, [activeUserId, api, isCurrentOwner, ownerGeneration])

  const confirmVipPayment = useCallback(async (orderId: string, signal?: AbortSignal): Promise<PaymentOrder> => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!owner || busy.current || !isCurrentOwner(generation, owner)) throw new Error('登录状态已变化，请重新打开订单。')
    busy.current = true
    setRecord(previous => ({ ...previous, owner, generation, error: '' }))
    try {
      const order = await api.refreshPayment(orderId, signal)
      if (!isCurrentOwner(generation, owner)) throw new Error('登录状态已变化，请重新打开订单。')
      if (order.status === 'paid') {
        const tier = order.productId === 'sun_svip' ? 'svip' : 'vip'
        setRecord(previous => ({ ...previous, vip: 'active', svip: tier === 'svip' ? 'active' : previous.svip, error: '' }))
      }
      return order
    } catch (error) {
      if (!signal?.aborted && isCurrentOwner(generation, owner)) setRecord(previous => ({ ...previous, error: error instanceof Error ? error.message : '支付状态查询失败，请重试。' }))
      throw error
    } finally {
      busy.current = false
    }
  }, [activeUserId, api, isCurrentOwner, ownerGeneration])

  const getVipPayment = useCallback(async (orderId: string, signal?: AbortSignal): Promise<PaymentOrder> => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!owner || !isCurrentOwner(generation, owner)) throw new Error('登录状态已变化，请重新打开订单。')
    return api.getPayment(orderId, signal)
  }, [activeUserId, api, isCurrentOwner, ownerGeneration])

  const refreshEntitlements = useCallback(async () => {
    const owner = activeUserId
    const generation = ownerGeneration
    if (!owner || !isCurrentOwner(generation, owner)) return
    try {
      const items = await api.getEntitlements()
      if (!isCurrentOwner(generation, owner)) return
      const svip = items.some(item => item.productId === 'sun_svip' && item.status === 'active')
      const vip = svip || items.some(item => item.productId === 'sun_vip' && item.status === 'active')
      setRecord({ owner, generation, vip: vip ? 'active' : 'inactive', svip: svip ? 'active' : 'inactive', error: '' })
    } catch (error) {
      if (isCurrentOwner(generation, owner)) setRecord(previous => ({ ...previous, error: error instanceof Error ? error.message : '权益服务暂时不可用' }))
    }
  }, [activeUserId, api, isCurrentOwner, ownerGeneration])

  return { vipStatus: current ? record.vip : 'unknown' as VipStatus, svipStatus: current ? record.svip : 'unknown' as VipStatus, vipError: current ? record.error : '', beginVipPayment, getVipPayment, confirmVipPayment, refreshEntitlements }
}
